import { App, watch } from 'vue';

import Toast, { PluginOptions, POSITION } from 'vue-toastification';
import { createI18n } from 'vue-i18n';

import he from '@app/i18n/locales/he';
import en from '@app/i18n/locales/en';
import ar from '@app/i18n/locales/ar';
import ru from '@app/i18n/locales/ru';

const LOCALE_STORAGE_KEY = 'torim-locale';
const RTL_LOCALES = ['he', 'ar'];

export type SupportedLocale = 'he' | 'en' | 'ar' | 'ru';

export const SUPPORTED_LOCALES: { code: SupportedLocale; label: string; flag: string }[] = [
  { code: 'he', label: 'עברית', flag: '🇮🇱' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
];

export const getSavedLocale = (): SupportedLocale => {
  if (import.meta.env.SSR) return 'he';

  const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (saved && ['he', 'en', 'ar', 'ru'].includes(saved)) {
    return saved as SupportedLocale;
  }
  return 'he';
};

export const saveLocale = (locale: SupportedLocale) => {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
};

export const applyDirection = (locale: string) => {
  if (import.meta.env.SSR) return;

  const dir = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', locale);
};

export const applyToast = (app: App) => {
  app.use(Toast, <PluginOptions>{
    position: POSITION.TOP_CENTER,
    rtl: true,
    toastClassName: 'torim-toast',
    timeout: 2000,
  });
};

let i18nInstance: ReturnType<typeof createI18n>;

export const getI18n = () => i18nInstance;

export const applyI18n = (app: App) => {
  const savedLocale = getSavedLocale();

  i18nInstance = createI18n({
    legacy: false,
    locale: savedLocale,
    fallbackLocale: 'he',
    messages: { he, en, ar, ru },
  });

  applyDirection(savedLocale);

  app.use(i18nInstance);
};
